import { axiosAdminApiClient, axiosApiClient } from "..";
import { handleError } from "../../utils/errors";

class WorkshopService {
  // Admin methods - requires authentication
  getAllWorkshopsForAdmin = async ({
    page = 1,
    limit = 10,
    status,
    type,
    category,
    instructor,
    search,
    dateFrom,
    dateTo,
    sortBy = "date",
    sortOrder = "ASC",
  } = {}) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      params.append("sortBy", sortBy);
      params.append("sortOrder", sortOrder);

      if (status) params.append("status", status);
      if (type) params.append("type", type);
      if (category) params.append("category", category);
      if (instructor) params.append("instructor", instructor);
      if (search) params.append("search", search);
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo) params.append("dateTo", dateTo);

      const response = await axiosAdminApiClient.get(
        `/workshops/admin?${params.toString()}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching workshops for admin.",
      );
    }
  };

  getWorkshopById = async (id) => {
    try {
      const response = await axiosAdminApiClient.get(`/workshops/${id}`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching workshop details.");
    }
  };

  createWorkshop = async (workshopData) => {
    try {
      const response = await axiosAdminApiClient.post(
        "/workshops",
        workshopData,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while creating workshop.");
    }
  };

  updateWorkshop = async (id, workshopData) => {
    try {
      const response = await axiosAdminApiClient.put(
        `/workshops/${id}`,
        workshopData,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while updating workshop.");
    }
  };

  deleteWorkshop = async (id) => {
    try {
      const response = await axiosAdminApiClient.delete(`/workshops/${id}`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting workshop.");
    }
  };

  getWorkshopRegistrations = async (id) => {
    try {
      const response = await axiosAdminApiClient.get(
        `/workshops/${id}/registrations`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching workshop registrations.",
      );
    }
  };

  updateRegistrationStatus = async (workshopId, registrationId, statusData) => {
    try {
      const response = await axiosAdminApiClient.put(
        `/workshops/${workshopId}/registrations/${registrationId}`,
        statusData,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while updating registration status.",
      );
    }
  };

  getWorkshopsStatistics = async () => {
    try {
      const response = await axiosAdminApiClient.get("/workshops/statistics");
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching workshops statistics.",
      );
    }
  };

  // Public methods - no authentication required
  getPublicWorkshops = async ({
    page = 1,
    limit = 10,
    status = "active",
    type,
    category,
    dateFrom,
    dateTo,
  } = {}) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      params.append("status", status);

      if (type) params.append("type", type);
      if (category) params.append("category", category);
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo) params.append("dateTo", dateTo);

      const response = await axiosApiClient.get(
        `/workshops?${params.toString()}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching public workshops.");
    }
  };

  getPublicWorkshopById = async (id) => {
    try {
      const response = await axiosApiClient.get(`/workshops/public/${id}`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching public workshop details.",
      );
    }
  };

  registerForWorkshop = async (id, registrationData) => {
    try {
      const response = await axiosApiClient.post(
        `/workshops/${id}/register`,
        registrationData,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while registering for workshop.");
    }
  };

  getUserWorkshopRegistrations = async ({
    page = 1,
    limit = 10,
    status,
    sortBy = "createdAt",
    sortOrder = "DESC",
  } = {}) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      params.append("sortBy", sortBy);
      params.append("sortOrder", sortOrder);

      if (status) params.append("status", status);

      const response = await axiosApiClient.get(
        `/workshops/my-registrations?${params.toString()}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching user workshop registrations.",
      );
    }
  };

  sendZoomLinkToRegistrations = async (workshopId, zoomData) => {
    try {
      const response = await axiosAdminApiClient.post(
        `/workshops/${workshopId}/send-zoom-link`,
        zoomData,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while sending Zoom link to registrations.",
      );
    }
  };

  getWorkshopSchedulesBySKU = async (sku) => {
    console.log(sku);
    try {
      const response = await axiosApiClient.get(`/workshops/schedules/${sku}`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching workshop schedules by SKU.",
      );
    }
  };
}

const workshopService = new WorkshopService();

export default workshopService;
